/* ControlVehicularModule.jsx — Módulo Control Vehicular (Octavio 15-jun-2026).
   Catálogo de vehículos, edición, historial, carga/exportación Excel.
   Acceso: admin · control_vehicular. */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import UsoDiarioPanel from "./UsoDiarioPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  ACTIVO: { bg: "#D1FAE5", color: "#047857", icon: "fa-circle-check",     label: "Activo" },
  TALLER: { bg: "#FEF3C7", color: "#92400E", icon: "fa-screwdriver-wrench", label: "En taller" },
  BAJA:   { bg: "#FEE2E2", color: "#991B1B", icon: "fa-ban",                label: "Baja" },
};

export default function ControlVehicularModule() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [vehiculos, setVehiculos] = useState([]);
  const [contadores, setContadores] = useState({ total: 0, activos: 0, taller: 0, baja: 0 });
  const [filtroQ, setFiltroQ] = useState("");
  const [filtroDep, setFiltroDep] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  const [cargando, setCargando] = useState(false);

  const [modal, setModal] = useState(null); // {tipo: 'editar'|'historial'|'carga', vehiculo?}
  const [searchParams] = useSearchParams();
  // 'catalogo' | 'uso-diario' | 'historial-taller'. ?tab= desde URL (deep-link desde Hub).
  const [tab, setTab] = useState(() => {
    const t = searchParams.get("tab");
    return ["catalogo","uso-diario","historial-taller","importar-bitacoras"].includes(t) ? t : "catalogo";
  });

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = {};
      if (filtroQ.trim()) params.q = filtroQ.trim();
      if (filtroDep) params.departamento = filtroDep;
      if (filtroStatus) params.status = filtroStatus;
      const r = await axios.get(`${API}/vehiculos`, { params });
      setVehiculos(r.data.items || []);
      setContadores(r.data.contadores || { total: 0, activos: 0, taller: 0, baja: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando vehículos");
    } finally {
      setCargando(false);
    }
  }, [filtroQ, filtroDep, filtroStatus]);

  // Debounce en búsqueda
  useEffect(() => {
    const t = setTimeout(cargar, 280);
    return () => clearTimeout(t);
  }, [cargar]);

  const departamentos = useMemo(() => {
    const set = new Set();
    vehiculos.forEach((v) => v.departamento && set.add(v.departamento));
    return Array.from(set).sort();
  }, [vehiculos]);

  const exportar = async (tipo) => {
    try {
      const url = tipo === "catalogo"
        ? `${API}/vehiculos/exportar`
        : `${API}/vehiculos/historial/exportar`;
      const r = await axios.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const fname = tipo === "catalogo" ? "vehiculos_catalogo" : "vehiculos_historial";
      link.download = `${fname}_${new Date().toISOString().slice(0,10)}.xlsx`;
      link.click();
      toast.success("Excel descargado");
    } catch (e) {
      toast.error("Error al exportar");
    }
  };

  const limpiarCatalogo = async () => {
    if (!window.confirm("¿Seguro? Esto borrará todos los vehículos.")) return;
    try {
      const r = await axios.delete(`${API}/vehiculos/limpiar-catalogo`);
      toast.success(r.data.mensaje || "Catálogo limpiado. Carga el Excel de nuevo.");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al limpiar");
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F8FAFC", color: "#0F172A" }}>
      {/* Header */}
      <header style={{ background: "#0F172A", color: "white", padding: "12px 20px", display: "flex", alignItems: "center", gap: 16 }}>
        <i className="fa-solid fa-car" style={{ fontSize: 22, color: "#C9A84C" }}/>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16 }} data-testid="cv-titulo">Control Vehicular · DMU 614</div>
          <div style={{ fontSize: 11, color: "#94A3B8" }}>Catálogo · Resguardos · Historial</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: "#CBD5E1" }} data-testid="cv-usuario">
            {user?.nombre || user?.usuario} · {user?.rol}
          </span>
          {["admin","control_taller"].includes(user?.rol) && (
            <button onClick={() => navigate("/hub")}
                    data-testid="cv-volver-hub"
                    style={{ background: "#1E293B", color: "white", border: 0, padding: "6px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>
              <i className="fa-solid fa-arrow-left mr-1"/>Hub
            </button>
          )}
          <button onClick={logout} data-testid="cv-logout"
                  style={{ background: "transparent", border: "1px solid #475569", color: "#CBD5E1", padding: "6px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>
            Salir
          </button>
        </div>
      </header>

      {/* Tabs — admin: gold #C9A84C; control_taller: colores por sección (Octavio 24-ago-2026) */}
      <div style={{ background: "white", borderBottom: "1px solid #E2E8F0", padding: "0 16px", display: "flex", gap: 2 }}>
        {/* Flotilla — solo visible para control_taller; navega a /supervisor */}
        {user?.rol === "control_taller" && (() => {
          const color = "#0369A1";
          return (
            <button
              type="button"
              onClick={() => navigate("/supervisor")}
              data-testid="cv-tab-flotilla"
              style={{
                border: 0, padding: "10px 14px", fontSize: 13, fontWeight: 700,
                background: "transparent", color,
                borderBottom: "3px solid transparent",
                cursor: "pointer", transition: "background 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = color + "18")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <i className="fa-solid fa-truck-droplet mr-1"/>Flotilla
            </button>
          );
        })()}
        {/* Catálogo */}
        {(() => {
          const esCT = user?.rol === "control_taller";
          const color = esCT ? "#4C1D95" : "#C9A84C";
          const activo = tab === "catalogo";
          return (
            <button
              type="button"
              onClick={() => setTab("catalogo")}
              data-testid="cv-tab-catalogo"
              style={{
                border: 0, padding: "10px 14px", fontSize: 13, fontWeight: 700,
                borderRadius: activo ? "6px 6px 0 0" : 0,
                background: activo ? color : "transparent",
                color: activo ? (esCT ? "white" : "#0F172A") : (esCT ? color : "#94A3B8"),
                borderBottom: activo ? `3px solid ${color}` : "3px solid transparent",
                cursor: "pointer", transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { if (!activo) e.currentTarget.style.background = color + "18"; }}
              onMouseLeave={(e) => { if (!activo) e.currentTarget.style.background = "transparent"; }}
            >
              <i className="fa-solid fa-car mr-1"/>Catálogo
            </button>
          );
        })()}
        {/* Uso diario */}
        {(() => {
          const esCT = user?.rol === "control_taller";
          const color = esCT ? "#0F766E" : "#C9A84C";
          const activo = tab === "uso-diario";
          return (
            <button
              type="button"
              onClick={() => setTab("uso-diario")}
              data-testid="cv-tab-uso-diario"
              style={{
                border: 0, padding: "10px 14px", fontSize: 13, fontWeight: 700,
                borderRadius: activo ? "6px 6px 0 0" : 0,
                background: activo ? color : "transparent",
                color: activo ? (esCT ? "white" : "#0F172A") : (esCT ? color : "#94A3B8"),
                borderBottom: activo ? `3px solid ${color}` : "3px solid transparent",
                cursor: "pointer", transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { if (!activo) e.currentTarget.style.background = color + "18"; }}
              onMouseLeave={(e) => { if (!activo) e.currentTarget.style.background = "transparent"; }}
            >
              <i className="fa-solid fa-clipboard-list mr-1"/>Uso diario
            </button>
          );
        })()}
        {/* Historial Taller */}
        {(() => {
          const esCT = user?.rol === "control_taller";
          const color = esCT ? "#B45309" : "#C9A84C";
          const activo = tab === "historial-taller";
          return (
            <button
              type="button"
              onClick={() => setTab("historial-taller")}
              data-testid="cv-tab-historial-taller"
              style={{
                border: 0, padding: "10px 14px", fontSize: 13, fontWeight: 700,
                borderRadius: activo ? "6px 6px 0 0" : 0,
                background: activo ? color : "transparent",
                color: activo ? (esCT ? "white" : "#0F172A") : (esCT ? color : "#94A3B8"),
                borderBottom: activo ? `3px solid ${color}` : "3px solid transparent",
                cursor: "pointer", transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { if (!activo) e.currentTarget.style.background = color + "18"; }}
              onMouseLeave={(e) => { if (!activo) e.currentTarget.style.background = "transparent"; }}
            >
              <i className="fa-solid fa-clock-rotate-left mr-1"/>Historial Taller
            </button>
          );
        })()}
        {/* Importar bitácoras — solo admin y control_taller */}
        {(() => {
          const esCT = user?.rol === "control_taller";
          const color = "#0F766E";
          const activo = tab === "importar-bitacoras";
          return (
            <button
              type="button"
              onClick={() => setTab("importar-bitacoras")}
              data-testid="cv-tab-importar-bitacoras"
              style={{
                border: 0, padding: "10px 14px", fontSize: 13, fontWeight: 700,
                borderRadius: activo ? "6px 6px 0 0" : 0,
                background: activo ? color : "transparent",
                color: activo ? "white" : (esCT ? color : "#94A3B8"),
                borderBottom: activo ? `3px solid ${color}` : "3px solid transparent",
                cursor: "pointer", transition: "background 0.15s",
              }}
              onMouseEnter={(e) => { if (!activo) e.currentTarget.style.background = color + "18"; }}
              onMouseLeave={(e) => { if (!activo) e.currentTarget.style.background = "transparent"; }}
            >
              <i className="fa-solid fa-file-arrow-up mr-1"/>Importar bitácoras
            </button>
          );
        })()}
      </div>

      {tab === "uso-diario" && (
        <div style={{ padding: "16px 20px" }}>
          <UsoDiarioPanel/>
        </div>
      )}

      {tab === "historial-taller" && <TabHistorialTaller />}
      {tab === "importar-bitacoras" && <TabImportarBitacoras />}

      {tab === "catalogo" && (
      <>
      {/* Barra de filtros + acciones */}
      <div style={{ padding: "16px 20px", background: "white", borderBottom: "1px solid #E2E8F0" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <div style={{ position: "relative", flex: "1 1 260px", maxWidth: 360 }}>
            <i className="fa-solid fa-magnifying-glass"
               style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94A3B8", fontSize: 12 }}/>
            <input type="text" value={filtroQ} onChange={(e) => setFiltroQ(e.target.value)}
                   placeholder="Buscar No. Eco, descripción, placas, empleado…"
                   data-testid="cv-buscar"
                   style={{ width: "100%", padding: "8px 12px 8px 30px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13 }}/>
          </div>
          <select value={filtroDep} onChange={(e) => setFiltroDep(e.target.value)}
                  data-testid="cv-filtro-dep"
                  style={{ padding: "8px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, background: "white", minWidth: 160 }}>
            <option value="">Todos los departamentos</option>
            {departamentos.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}
                  data-testid="cv-filtro-status"
                  style={{ padding: "8px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, background: "white", minWidth: 130 }}>
            <option value="">Todos los status</option>
            <option value="ACTIVO">Activo</option>
            <option value="TALLER">En taller</option>
            <option value="BAJA">Baja</option>
          </select>
          <div style={{ flex: 1 }}/>
          {user?.rol !== "control_taller" && (<>
          <button onClick={() => setModal({ tipo: "carga" })}
                  data-testid="cv-btn-cargar-excel"
                  style={{ background: "#15803D", color: "white", border: 0, padding: "8px 14px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            <i className="fa-solid fa-upload mr-1"/>Cargar Excel
          </button>
          </>)}
          <button onClick={() => exportar("catalogo")}
                  data-testid="cv-btn-exportar-cat"
                  style={{ background: "#1D4ED8", color: "white", border: 0, padding: "8px 14px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            <i className="fa-solid fa-file-arrow-down mr-1"/>Exportar catálogo
          </button>
          {user?.rol !== "control_taller" && (<>
          <button onClick={() => exportar("historial")}
                  data-testid="cv-btn-exportar-hist"
                  style={{ background: "#7C3AED", color: "white", border: 0, padding: "8px 14px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
            <i className="fa-solid fa-clock-rotate-left mr-1"/>Exportar historial
          </button>
          </>)}
          {user?.rol === "admin" && (
            <button onClick={limpiarCatalogo}
                    data-testid="cv-btn-limpiar-catalogo"
                    title="Borra TODOS los vehículos del catálogo"
                    style={{ background: "#DC2626", color: "white", border: 0, padding: "8px 14px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              <i className="fa-solid fa-trash mr-1"/>Limpiar catálogo
            </button>
          )}
        </div>

        {/* Contadores */}
        <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
          <Contador label="Total" valor={contadores.total} color="#0F172A" testid="cv-cnt-total"/>
          <Contador label="Activas" valor={contadores.activos} color="#047857" testid="cv-cnt-activos"/>
          <Contador label="En taller" valor={contadores.taller} color="#92400E" testid="cv-cnt-taller"/>
          <Contador label="Baja" valor={contadores.baja} color="#991B1B" testid="cv-cnt-baja"/>
          <div style={{ marginLeft: "auto", fontSize: 12, color: "#64748B" }} data-testid="cv-resultado">
            {cargando ? "Cargando…" : `${vehiculos.length} resultado(s)`}
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div style={{ padding: "16px 20px" }}>
        {vehiculos.length === 0 && !cargando && (
          <div style={{ background: "white", padding: 40, textAlign: "center", borderRadius: 10, border: "1px dashed #CBD5E1", color: "#64748B" }}
               data-testid="cv-vacio">
            <i className="fa-solid fa-car-side" style={{ fontSize: 40, color: "#CBD5E1", marginBottom: 12, display: "block" }}/>
            {filtroQ || filtroDep || filtroStatus
              ? "Sin resultados con esos filtros."
              : "No hay vehículos cargados. Da clic en 'Cargar Excel' para empezar."}
          </div>
        )}
        {vehiculos.length > 0 && (
          <div style={{ background: "white", borderRadius: 10, overflow: "auto", border: "1px solid #E2E8F0" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead style={{ background: "#F1F5F9", position: "sticky", top: 0 }}>
                <tr>
                  <Th>No. Eco</Th>
                  <Th>Descripción</Th>
                  <Th>Marca/Modelo</Th>
                  <Th>Placas</Th>
                  <Th>Departamento</Th>
                  <Th>Resguardante</Th>
                  <Th align="center">Odómetro</Th>
                  <Th align="center">Status</Th>
                  <Th>Última act.</Th>
                  <Th align="center">Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {vehiculos.map((v) => {
                  const st = STATUS_BADGE[v.status] || STATUS_BADGE.ACTIVO;
                  return (
                    <tr key={v.no_economico} data-testid={`cv-fila-${v.no_economico}`}
                        style={{ borderTop: "1px solid #F1F5F9" }}>
                      <Td><strong>{v.no_economico}</strong></Td>
                      <Td>{v.descripcion || "—"}</Td>
                      <Td>{v.marca} {v.modelo}</Td>
                      <Td><span style={{ fontFamily: "monospace", fontWeight: 700 }}>{v.placas || "—"}</span></Td>
                      <Td>{v.departamento || "—"}</Td>
                      <Td>
                        <div>{v.resguardante || "—"}</div>
                      </Td>
                      <Td align="center" data-testid={`cv-odom-${v.no_economico}`}>
                        {v.no_empleado ? v.no_empleado : "—"}
                      </Td>
                      <Td align="center">
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: st.bg, color: st.color }}
                              data-testid={`cv-status-${v.no_economico}`}>
                          <i className={`fa-solid ${st.icon}`}/> {st.label}
                        </span>
                      </Td>
                      <Td>
                        <div style={{ fontSize: 11 }}>{(v.ultima_actualizacion || "").slice(0, 10) || "—"}</div>
                        <div style={{ fontSize: 10, color: "#64748B" }}>{v.actualizado_por || ""}</div>
                      </Td>
                      <Td align="center">
                        <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                          <IconButton onClick={() => setModal({ tipo: "editar", vehiculo: v })}
                                      testid={`cv-editar-${v.no_economico}`} title="Editar" icon="fa-pen" color="#1D4ED8"/>
                          <IconButton onClick={() => setModal({ tipo: "historial", vehiculo: v })}
                                      testid={`cv-historial-${v.no_economico}`} title="Historial" icon="fa-clock-rotate-left" color="#7C3AED"/>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal?.tipo === "editar" && (
        <ModalEditar vehiculo={modal.vehiculo} onClose={() => setModal(null)} onSave={() => { setModal(null); cargar(); }}/>
      )}
      {modal?.tipo === "historial" && (
        <ModalHistorial vehiculo={modal.vehiculo} onClose={() => setModal(null)}/>
      )}
      {modal?.tipo === "carga" && (
        <ModalCargaExcel onClose={() => setModal(null)} onDone={() => { setModal(null); cargar(); }}/>
      )}
      </>
      )}
    </div>
  );
}

/* ─── Componentes auxiliares ─── */
function Contador({ label, valor, color, testid }) {
  return (
    <div data-testid={testid} style={{ background: "white", border: "1px solid #E2E8F0", padding: "6px 14px", borderRadius: 8, minWidth: 90 }}>
      <div style={{ fontSize: 10, color: "#64748B", fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{valor}</div>
    </div>
  );
}
function Th({ children, align = "left" }) {
  return <th style={{ padding: "10px 12px", textAlign: align, fontSize: 11, fontWeight: 800, color: "#475569", textTransform: "uppercase", whiteSpace: "nowrap" }}>{children}</th>;
}
function Td({ children, align = "left" }) {
  return <td style={{ padding: "10px 12px", textAlign: align, color: "#0F172A", verticalAlign: "top" }}>{children}</td>;
}
function IconButton({ onClick, icon, color, title, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} title={title}
            style={{ background: "white", border: `1px solid ${color}`, color, padding: "5px 9px", borderRadius: 6, cursor: "pointer", fontSize: 11 }}>
      <i className={`fa-solid ${icon}`}/>
    </button>
  );
}

/* ─── Tab: Historial Taller — entradas/salidas de pipas (control_taller · Octavio 21-ago-2026) ─── */
function TabHistorialTaller() {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    axios.get(`${API}/coberturas/pipas-taller-historial`)
      .then(r => setItems(r.data.items || []))
      .catch(() => {})
      .finally(() => setCargando(false));
  }, []);

  const fmtFecha = (iso) => {
    if (!iso) return "—";
    // Solo fecha, sin hora (el usuario captura fecha de entrada manualmente)
    const [y, m, d] = (iso.slice(0, 10)).split("-").map(Number);
    return `${d}/${m}/${y}`;
  };

  const diasEnTaller = (entrada, salida) => {
    if (!entrada) return "";
    const fin = salida ? new Date(salida) : new Date();
    const dias = ((fin - new Date(entrada)) / 86400000).toFixed(1);
    return `${dias}d`;
  };

  return (
    <div style={{ padding: "16px 20px" }}>
      <div style={{ marginBottom: 12, fontSize: 13, color: "#64748B" }}>
        <i className="fa-solid fa-clock-rotate-left mr-1"/>
        Registro de entradas y salidas de taller · {items.length} registros
      </div>
      {cargando && <div style={{ textAlign: "center", padding: 40, color: "#94A3B8" }}>Cargando…</div>}
      {!cargando && items.length === 0 && (
        <div style={{ textAlign: "center", padding: 40, color: "#94A3B8" }}>Sin registros de taller.</div>
      )}
      {!cargando && items.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#F8FAFC", borderBottom: "2px solid #E2E8F0" }}>
                {["Pipa","Lugar","Motivo","Entrada","Salida","Tiempo","Responsable"].map(h => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, color: "#475569", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const enTaller = !it.fecha_salida;
                return (
                  <tr key={it.id || i} style={{ borderBottom: "1px solid #F1F5F9", background: i % 2 === 0 ? "white" : "#FAFAFA" }}>
                    <td style={{ padding: "7px 10px", fontWeight: 700 }}>{it.pipa_numero || it.id_pipa || it.pipa_id || "—"}</td>
                    <td style={{ padding: "7px 10px", color: "#475569" }}>{it.lugar_taller || it.lugar || "—"}</td>
                    <td style={{ padding: "7px 10px", color: "#475569", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.motivo || "—"}</td>
                    <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>{fmtFecha(it.fecha_entrada)}</td>
                    <td style={{ padding: "7px 10px", whiteSpace: "nowrap" }}>
                      {enTaller
                        ? <span style={{ background: "#FEF3C7", color: "#92400E", padding: "2px 7px", borderRadius: 99, fontWeight: 700, fontSize: 11 }}>En taller</span>
                        : fmtFecha(it.fecha_salida)
                      }
                    </td>
                    <td style={{ padding: "7px 10px", color: "#64748B" }}>{diasEnTaller(it.fecha_entrada, it.fecha_salida)}</td>
                    <td style={{ padding: "7px 10px", color: "#64748B" }}>{it.usuario_nombre || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Tab: Importar bitácoras de taller desde Excel ─── */
// Tabla fija de traducción folio-interno → número económico real
// Fuente: Catalogo_Maestro_Pipas.xlsx hoja PIPAS_FANTASMA, 28-ago-2026
const FOLIO_A_ECO = {
  "10255": "301", "10256": "302", "10257": "309", "10258": "316",
  "10259": "325", "10260": "326", "10262": "334", "10263": "335",
  "10264": "381",
};

function TabImportarBitacoras() {
  // Lista de filas: { file, nombre, preview: null|{...}, noEco: string, descartado: bool, importando: bool, resultado: null|{...} }
  const [filas, setFilas] = useState([]);
  const [cargandoPreview, setCargandoPreview] = useState(false);
  const [importando, setImportando] = useState(false);

  const setFila = (idx, patch) =>
    setFilas(prev => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));

  // Al seleccionar archivos → crear filas y pedir preview a cada uno
  const onSeleccionarArchivos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";

    setCargandoPreview(true);
    const nuevas = files.map(f => ({
      file: f,
      nombre: f.name,
      preview: null,
      noEco: "",
      descartado: false,
      importando: false,
      resultado: null,
    }));
    setFilas(prev => [...prev, ...nuevas]);
    const base = filas.length;

    await Promise.all(nuevas.map(async (fila, rel) => {
      const idx = base + rel;
      try {
        const fd = new FormData();
        fd.append("file", fila.file);
        const r = await axios.post(
          `${API}/admin/preview-bitacora-taller`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        const p = r.data;
        // Prellenar noEco con la traducción automática (o vacío si hay discrepancia)
        const noEcoAuto = p.discrepancia_contenido ? "" : (p.no_economico_traducido || "");
        setFilas(prev => prev.map((f, i) =>
          i === idx ? { ...f, preview: p, noEco: noEcoAuto } : f
        ));
      } catch (err) {
        setFilas(prev => prev.map((f, i) =>
          i === idx ? { ...f, preview: { error: err?.response?.data?.detail || "Error al parsear" } } : f
        ));
      }
    }));
    setCargandoPreview(false);
  };

  // Importar todos los confirmados (noEco relleno, no descartados)
  const onImportar = async () => {
    const pendientes = filas.filter(f => f.noEco.trim() && !f.descartado && !f.resultado);
    if (!pendientes.length) {
      toast.error("No hay archivos confirmados para importar");
      return;
    }
    setImportando(true);
    for (let i = 0; i < filas.length; i++) {
      const f = filas[i];
      if (!f.noEco.trim() || f.descartado || f.resultado) continue;
      setFila(i, { importando: true });
      try {
        const fd = new FormData();
        fd.append("file", f.file);
        const r = await axios.post(
          `${API}/admin/importar-bitacora-taller?no_economico_confirmado=${encodeURIComponent(f.noEco.trim())}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setFila(i, { importando: false, resultado: r.data });
      } catch (err) {
        setFila(i, {
          importando: false,
          resultado: { ok: false, error: err?.response?.data?.detail || "Error" }
        });
      }
    }
    setImportando(false);
    toast.success("Importación completada — revisa los resultados");
  };

  const limpiar = () => setFilas([]);
  const confirmados = filas.filter(f => f.noEco.trim() && !f.descartado && !f.resultado).length;

  return (
    <div style={{ padding: "20px 20px" }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#0F766E", marginBottom: 4 }}>
          <i className="fa-solid fa-file-arrow-up mr-2"/>Importar bitácoras de taller desde Excel
        </div>
        <div style={{ fontSize: 12, color: "#64748B", maxWidth: 680 }}>
          Selecciona uno o varios archivos <code>.xlsx</code>. El sistema detectará
          la unidad automáticamente. Los archivos con <strong>contenido cruzado</strong>{" "}
          quedarán en rojo — debes escribir el número correcto a mano o descartarlos.
        </div>
      </div>

      {/* Selector de archivos */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16 }}>
        <label style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: "#0F766E", color: "white", padding: "8px 14px",
          borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer",
        }}>
          <i className="fa-solid fa-folder-open"/>
          Seleccionar archivos .xlsx
          <input type="file" accept=".xlsx,.xls" multiple hidden onChange={onSeleccionarArchivos} />
        </label>
        {filas.length > 0 && (
          <button onClick={limpiar} style={{ fontSize: 12, color: "#DC2626", background: "none", border: "none", cursor: "pointer" }}>
            Limpiar todo
          </button>
        )}
        {cargandoPreview && (
          <span style={{ fontSize: 12, color: "#64748B" }}>
            <i className="fa-solid fa-spinner fa-spin mr-1"/>Analizando archivos…
          </span>
        )}
      </div>

      {/* Tabla de validación */}
      {filas.length > 0 && (
        <>
          <div style={{ overflowX: "auto", marginBottom: 16 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "#F8FAFC", borderBottom: "2px solid #E2E8F0" }}>
                  {["Archivo","Detectado","Confirmar unidad","Filas","Estatus","Acción"].map(h => (
                    <th key={h} style={{ padding: "8px 10px", textAlign: "left", fontWeight: 700, color: "#475569", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filas.map((f, i) => {
                  const p = f.preview;
                  const error = p?.error;
                  const discrepancia = p?.discrepancia_contenido;
                  const rowBg = f.descartado
                    ? "#F8FAFC"
                    : discrepancia && !f.noEco
                    ? "#FEF2F2"
                    : f.resultado?.ok === false
                    ? "#FEF2F2"
                    : f.resultado?.ok
                    ? "#F0FDF4"
                    : "white";

                  return (
                    <tr key={i} style={{ borderBottom: "1px solid #F1F5F9", background: rowBg, opacity: f.descartado ? 0.5 : 1 }}>
                      {/* Archivo */}
                      <td style={{ padding: "8px 10px", color: "#334155", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={f.nombre}>
                        <i className="fa-solid fa-file-excel mr-1" style={{ color: "#16A34A" }}/>
                        {f.nombre}
                      </td>

                      {/* Detectado */}
                      <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                        {!p && <span style={{ color: "#94A3B8" }}><i className="fa-solid fa-spinner fa-spin mr-1"/>…</span>}
                        {error && <span style={{ color: "#DC2626" }}>{error}</span>}
                        {p && !error && (
                          <div>
                            <div style={{ fontWeight: 700 }}>
                              Eco: {p.no_economico_traducido || "—"}
                            </div>
                            {p.folio_detectado_en_nombre && (
                              <div style={{ fontSize: 10, color: "#64748B" }}>folio: {p.folio_detectado_en_nombre}</div>
                            )}
                            {p.numero_unidad_en_texto_interno && (
                              <div style={{ fontSize: 10, color: "#64748B" }}>interno: {p.numero_unidad_en_texto_interno}</div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Confirmar unidad */}
                      <td style={{ padding: "8px 10px" }}>
                        {f.resultado ? (
                          <span style={{ fontWeight: 700, color: "#0F766E" }}>{f.noEco}</span>
                        ) : (
                          <div>
                            <input
                              type="text"
                              placeholder={discrepancia ? "¿Cuál es la unidad real? (oblig.)" : "Número económico"}
                              value={f.noEco}
                              onChange={e => setFila(i, { noEco: e.target.value })}
                              disabled={f.descartado || f.importando}
                              style={{
                                padding: "5px 8px", borderRadius: 6, fontSize: 12,
                                border: `1.5px solid ${discrepancia && !f.noEco ? "#DC2626" : "#CBD5E1"}`,
                                width: 140,
                              }}
                            />
                            {discrepancia && (
                              <div style={{ fontSize: 10, color: "#DC2626", marginTop: 2, maxWidth: 200 }}>
                                <i className="fa-solid fa-triangle-exclamation mr-1"/>
                                {p?.alerta}
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Filas */}
                      <td style={{ padding: "8px 10px", textAlign: "center", fontWeight: 700 }}>
                        {p && !error ? p.total_filas_detectadas : "—"}
                      </td>

                      {/* Estatus */}
                      <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                        {f.descartado && (
                          <span style={{ background: "#F1F5F9", color: "#64748B", padding: "2px 8px", borderRadius: 99, fontSize: 11 }}>Descartado</span>
                        )}
                        {!f.descartado && !p && (
                          <span style={{ color: "#94A3B8", fontSize: 11 }}>Analizando…</span>
                        )}
                        {!f.descartado && p && !error && !f.resultado && (
                          discrepancia && !f.noEco
                            ? <span style={{ background: "#FEE2E2", color: "#991B1B", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700 }}>⚠ Revisar</span>
                            : f.noEco
                            ? <span style={{ background: "#D1FAE5", color: "#047857", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700 }}>✓ OK</span>
                            : <span style={{ background: "#FEF3C7", color: "#92400E", padding: "2px 8px", borderRadius: 99, fontSize: 11 }}>Pendiente</span>
                        )}
                        {!f.descartado && f.importando && (
                          <span style={{ color: "#64748B", fontSize: 11 }}><i className="fa-solid fa-spinner fa-spin mr-1"/>Importando…</span>
                        )}
                        {!f.descartado && f.resultado && f.resultado.ok !== false && (
                          <span style={{ background: "#D1FAE5", color: "#047857", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700 }}>
                            ✓ {f.resultado.insertados} nuevas · {f.resultado.ya_existian} ya existían
                          </span>
                        )}
                        {!f.descartado && f.resultado && f.resultado.ok === false && (
                          <span style={{ background: "#FEE2E2", color: "#991B1B", padding: "2px 8px", borderRadius: 99, fontSize: 11 }}>
                            Error: {f.resultado.error}
                          </span>
                        )}
                        {!f.descartado && error && (
                          <span style={{ background: "#FEE2E2", color: "#991B1B", padding: "2px 8px", borderRadius: 99, fontSize: 11 }}>Error parseo</span>
                        )}
                      </td>

                      {/* Acción */}
                      <td style={{ padding: "8px 10px" }}>
                        {!f.resultado && (
                          <button
                            onClick={() => setFila(i, { descartado: !f.descartado })}
                            style={{
                              fontSize: 11, padding: "3px 8px", borderRadius: 6, cursor: "pointer",
                              background: f.descartado ? "#F0FDF4" : "#FEF2F2",
                              color: f.descartado ? "#047857" : "#DC2626",
                              border: `1px solid ${f.descartado ? "#BBF7D0" : "#FECACA"}`,
                            }}
                          >
                            {f.descartado ? "Restaurar" : "Descartar"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Vista previa de primeras 3 filas del primero OK (colapsable info) */}
          {(() => {
            const primero = filas.find(f => f.preview && !f.preview.error && f.preview.preview_primeras_3?.length);
            if (!primero) return null;
            const rows3 = primero.preview.preview_primeras_3;
            return (
              <details style={{ marginBottom: 16 }}>
                <summary style={{ fontSize: 12, color: "#64748B", cursor: "pointer" }}>
                  Vista previa — primeras 3 filas de "{primero.nombre}"
                </summary>
                <div style={{ overflowX: "auto", marginTop: 8 }}>
                  <table style={{ fontSize: 11, borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#F8FAFC" }}>
                        {["Fecha","Servicio","Realizada en","Estatus","Orden","Factura","Fecha factura"].map(h => (
                          <th key={h} style={{ padding: "5px 8px", border: "1px solid #E2E8F0", textAlign: "left", fontWeight: 700 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows3.map((r, ri) => (
                        <tr key={ri}>
                          {[r.fecha,r.servicio,r.realizada_en,r.estatus,r.orden,r.factura,r.fecha_factura].map((v, vi) => (
                            <td key={vi} style={{ padding: "4px 8px", border: "1px solid #F1F5F9", color: "#334155" }}>{v || "—"}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            );
          })()}

          {/* Botón importar */}
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={onImportar}
              disabled={importando || confirmados === 0}
              style={{
                padding: "10px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700,
                background: confirmados > 0 ? "#0F766E" : "#94A3B8",
                color: "white", border: "none",
                cursor: confirmados > 0 ? "pointer" : "default",
              }}
            >
              {importando
                ? <><i className="fa-solid fa-spinner fa-spin mr-1"/>Importando…</>
                : <><i className="fa-solid fa-database mr-1"/>Importar {confirmados} archivo{confirmados !== 1 ? "s" : ""} confirmado{confirmados !== 1 ? "s" : ""}</>
              }
            </button>
            <span style={{ fontSize: 12, color: "#64748B" }}>
              {filas.filter(f => f.descartado).length > 0 &&
                `${filas.filter(f => f.descartado).length} descartado(s) no se importarán`}
            </span>
          </div>
        </>
      )}

      {filas.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#94A3B8" }}>
          <i className="fa-solid fa-file-arrow-up" style={{ fontSize: 32, marginBottom: 8, display: "block" }}/>
          Selecciona uno o varios archivos .xlsx para comenzar
        </div>
      )}
    </div>
  );
}

/* ─── Modal: Editar Vehículo ─── */
function ModalEditar({ vehiculo, onClose, onSave }) {
  const [form, setForm] = useState({
    departamento:    vehiculo.departamento || "",
    resguardante:    vehiculo.resguardante || "",
    no_empleado:     vehiculo.no_empleado || "",
    placas:          vehiculo.placas || "",
    status:          vehiculo.status || "ACTIVO",
    odometro_activo: vehiculo.odometro_activo !== false,
    motivo:          "",
  });
  const [guardando, setGuardando] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const guardar = async () => {
    if (!form.motivo.trim()) {
      toast.error("Motivo obligatorio");
      return;
    }
    setGuardando(true);
    try {
      const r = await axios.put(`${API}/vehiculos/${encodeURIComponent(vehiculo.no_economico)}`, form);
      toast.success(r.data.mensaje || "Guardado");
      onSave?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <ModalShell title={`Editar vehículo #${vehiculo.no_economico}`} onClose={onClose} testid="cv-modal-editar">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Departamento" value={form.departamento} onChange={(v) => set("departamento", v)} testid="cv-edit-dep"/>
        <Field label="Resguardante / Conductor" value={form.resguardante} onChange={(v) => set("resguardante", v)} testid="cv-edit-resg"/>
        <Field label="No. Empleado" value={form.no_empleado} onChange={(v) => set("no_empleado", v)} testid="cv-edit-emp"/>
        <Field label="Placas" value={form.placas} onChange={(v) => set("placas", v)} testid="cv-edit-placas"/>
        <div>
          <Label>Status</Label>
          <select value={form.status} onChange={(e) => set("status", e.target.value)}
                  data-testid="cv-edit-status"
                  style={inputStyle}>
            <option value="ACTIVO">Activo</option>
            <option value="TALLER">En taller</option>
            <option value="BAJA">Baja</option>
          </select>
        </div>
        <div>
          <Label>Odómetro activo</Label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0" }}>
            <input type="checkbox" checked={form.odometro_activo}
                   onChange={(e) => set("odometro_activo", e.target.checked)}
                   data-testid="cv-edit-odom"/>
            <span style={{ fontSize: 13 }}>{form.odometro_activo ? "Activo (ON)" : "Inactivo (OFF)"}</span>
          </label>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <Label>Motivo del cambio <span style={{ color: "#DC2626" }}>*</span></Label>
          <textarea value={form.motivo} onChange={(e) => set("motivo", e.target.value)}
                    data-testid="cv-edit-motivo"
                    placeholder="Ej. Cambio de resguardo por reasignación de departamento"
                    rows={3}
                    style={{ ...inputStyle, resize: "vertical" }}/>
        </div>
      </div>
      <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onClose} style={btnSecundario} data-testid="cv-edit-cancelar">Cancelar</button>
        <button onClick={guardar} disabled={guardando} style={btnPrimario(guardando)} data-testid="cv-edit-guardar">
          <i className="fa-solid fa-floppy-disk mr-1"/>{guardando ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>
    </ModalShell>
  );
}

/* ─── Modal: Historial por Unidad ─── */
function ModalHistorial({ vehiculo, onClose }) {
  const [hist, setHist] = useState(null);
  useEffect(() => {
    axios.get(`${API}/vehiculos/${encodeURIComponent(vehiculo.no_economico)}`)
      .then((r) => setHist(r.data.historial || []))
      .catch(() => setHist([]));
  }, [vehiculo.no_economico]);

  const exportarUnidad = async () => {
    try {
      const r = await axios.get(`${API}/vehiculos/historial/exportar`, {
        params: { no_economico: vehiculo.no_economico },
        responseType: "blob",
      });
      const blob = new Blob([r.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `historial_${vehiculo.no_economico}_${new Date().toISOString().slice(0,10)}.xlsx`;
      link.click();
    } catch (e) {
      toast.error("Error al exportar");
    }
  };

  return (
    <ModalShell title={`Historial · #${vehiculo.no_economico}`} onClose={onClose} testid="cv-modal-historial" wide>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ color: "#64748B", fontSize: 12 }}>{vehiculo.descripcion} · {vehiculo.marca} {vehiculo.modelo}</div>
        <button onClick={exportarUnidad} data-testid="cv-hist-exportar"
                style={{ background: "#7C3AED", color: "white", border: 0, padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          <i className="fa-solid fa-file-arrow-down mr-1"/>Exportar historial de esta unidad
        </button>
      </div>
      {hist === null && <div style={{ color: "#64748B" }}>Cargando…</div>}
      {hist && hist.length === 0 && <div style={{ color: "#64748B", padding: 20, textAlign: "center" }}>Sin movimientos registrados.</div>}
      {hist && hist.length > 0 && (
        <div style={{ maxHeight: 480, overflowY: "auto", border: "1px solid #E2E8F0", borderRadius: 8 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead style={{ background: "#F1F5F9", position: "sticky", top: 0 }}>
              <tr>
                <Th>Fecha</Th><Th>Campo</Th><Th>Anterior</Th><Th>Nuevo</Th><Th>Modificado por</Th><Th>Motivo</Th>
              </tr>
            </thead>
            <tbody>
              {hist.map((h, i) => (
                <tr key={i} style={{ borderTop: "1px solid #F1F5F9" }}>
                  <Td>{(h.fecha_cambio || "").slice(0, 19).replace("T", " ")}</Td>
                  <Td><strong>{h.campo_modificado}</strong></Td>
                  <Td><span style={{ color: "#94A3B8" }}>{h.valor_anterior || "—"}</span></Td>
                  <Td><span style={{ color: "#047857", fontWeight: 700 }}>{h.valor_nuevo || "—"}</span></Td>
                  <Td>{h.modificado_por}</Td>
                  <Td><span style={{ fontSize: 11, color: "#64748B" }}>{h.motivo}</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ModalShell>
  );
}

/* ─── Modal: Carga Excel ─── */
function ModalCargaExcel({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const cargarPreview = async () => {
    if (!file) return;
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/vehiculos/carga-excel`, fd);
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al leer Excel");
    } finally {
      setEnviando(false);
    }
  };

  const confirmar = async () => {
    if (!file) return;
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/vehiculos/carga-excel?confirmar=true`, fd);
      toast.success(r.data.mensaje || "Cargado");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <ModalShell title="Cargar catálogo desde Excel" onClose={onClose} testid="cv-modal-carga" wide>
      {!preview && (
        <>
          <div style={{ padding: 16, background: "#F8FAFC", borderRadius: 8, border: "1px dashed #CBD5E1" }}>
            <Label>Archivo .xlsx</Label>
            <input type="file" accept=".xlsx,.xlsm"
                   onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }}
                   data-testid="cv-carga-archivo"
                   style={{ fontSize: 13 }}/>
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 8 }}>
              Columnas esperadas: <strong>No. ECONOMICO</strong>, No. ACTIVO, NUMERO DE SERIE,
              DESCRIPCION ACTIVO, MARCA, MODELO, PLACAS 2022/2024/2025, CILINDROS,
              DEPARTAMENTO, RESGUARDANTE, No. DE EMPLEADO.
            </div>
          </div>
          <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={onClose} style={btnSecundario} data-testid="cv-carga-cancelar">Cancelar</button>
            <button onClick={cargarPreview} disabled={!file || enviando} style={btnPrimario(!file || enviando)}
                    data-testid="cv-carga-preview">
              <i className="fa-solid fa-eye mr-1"/>{enviando ? "Leyendo…" : "Vista previa"}
            </button>
          </div>
        </>
      )}
      {preview && (
        <>
          <div style={{ padding: 12, background: "#EFF6FF", borderRadius: 8, fontSize: 13 }}>
            <strong>{preview.total_filas} fila(s)</strong> detectadas. Se cargarán al confirmar.
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 6 }}>
              Mapeo: {Object.entries(preview.mapeo_columnas || {}).map(([k, v]) => `${k}=${v}`).join(" · ")}
            </div>
          </div>
          <div style={{ marginTop: 12, maxHeight: 320, overflow: "auto", border: "1px solid #E2E8F0", borderRadius: 8 }}>
            <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
              <thead style={{ background: "#F1F5F9" }}>
                <tr>
                  <Th>No. Eco</Th><Th>Descripción</Th><Th>Marca</Th><Th>Modelo</Th>
                  <Th>Placas</Th><Th>Departamento</Th><Th>Resguardante</Th>
                </tr>
              </thead>
              <tbody>
                {(preview.primeras_5 || []).map((f, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #F1F5F9" }}>
                    <Td><strong>{f.no_economico}</strong></Td>
                    <Td>{f.descripcion}</Td>
                    <Td>{f.marca}</Td>
                    <Td>{f.modelo}</Td>
                    <Td>{f.placas}</Td>
                    <Td>{f.departamento}</Td>
                    <Td>{f.resguardante}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ padding: 8, color: "#64748B", fontSize: 11, textAlign: "center" }}>
              Mostrando primeras 5 de {preview.total_filas}.
            </div>
          </div>
          <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", gap: 8 }}>
            <button onClick={() => setPreview(null)} style={btnSecundario} data-testid="cv-carga-volver">
              <i className="fa-solid fa-arrow-left mr-1"/>Cambiar archivo
            </button>
            <button onClick={confirmar} disabled={enviando} style={btnPrimario(enviando)}
                    data-testid="cv-carga-confirmar">
              <i className="fa-solid fa-check mr-1"/>{enviando ? "Guardando…" : `Confirmar y cargar ${preview.total_filas}`}
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

/* ─── Shell de modal genérico ─── */
function ModalShell({ title, onClose, children, testid, wide }) {
  return (
    <div data-testid={testid}
         style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
      <div style={{ background: "white", borderRadius: 12, padding: 20, width: "100%", maxWidth: wide ? 960 : 600, maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#0F172A" }}>{title}</h2>
          <button onClick={onClose} data-testid={`${testid}-cerrar`}
                  style={{ background: "transparent", border: 0, fontSize: 18, color: "#94A3B8", cursor: "pointer", padding: 4 }}>
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputStyle = { width: "100%", padding: "8px 10px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 13, background: "white" };
const btnSecundario = { background: "white", color: "#475569", border: "1px solid #CBD5E1", padding: "8px 14px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" };
const btnPrimario = (disabled) => ({ background: disabled ? "#94A3B8" : "#1D4ED8", color: "white", border: 0, padding: "8px 14px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: disabled ? "not-allowed" : "pointer" });

function Label({ children }) {
  return <label style={{ display: "block", fontSize: 11, fontWeight: 800, color: "#475569", textTransform: "uppercase", marginBottom: 4 }}>{children}</label>;
}
function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} style={inputStyle}/>
    </div>
  );
}
